import swiperOptions from "../classes/lux-swiper/options";
import { initLuxDropdowns } from "./lux-dropdown";

const luxPopups = document.querySelectorAll("div.lux-popup");
const luxPopupToggles = document.querySelectorAll("*[data-toggle='lux-popup']");
const luxPopupNit = document.querySelectorAll(".lux-modal-nit, .lux-panel");
let luxToasts = document.querySelectorAll("div.lux-toast");
let luxModals = document.querySelectorAll("div.lux-modal");
/** @type {ReturnType<typeof setTimeout> | undefined} */
let delayTimer;

/**
 * Check whether an element has the "active" class.
 *
 * @param {Element} element - The element to check.
 * @returns {boolean} True if the element is active.
 */
function isActive(element) {
    return element.classList.contains("active");
}

/**
 * Close a modal/panel when Escape is pressed or its own backdrop is clicked.
 *
 * @param {Element} nitPopup - The modal/panel element to bind close behaviour to.
 * @returns {void}
 */
function bindNitClose(nitPopup) {
    document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && isActive(nitPopup)) {
            luxPopup.popupClose(nitPopup);
        }
    });

    nitPopup.addEventListener("click", function (e) {
        if (
            e.target instanceof Element &&
            e.target.matches(".lux-modal-nit, .lux-panel") &&
            isActive(nitPopup)
        ) {
            luxPopup.popupClose(nitPopup);
        }
    });
}

/**
 * Resolve a popup element by id, cloning it from a matching `<template>` the
 * first time it's needed. Popup markup that is only ever revealed by JS ships
 * inert inside a `<template>` so the browser skips style/layout work for it
 * until it's actually opened.
 *
 * @param {string} id - The popup element's id.
 * @returns {Element | null} The popup element, or null if neither an element nor a template for it exists.
 */
function ensurePopupElement(id) {
    const existing = document.getElementById(id);
    if (existing) {
        return existing;
    }

    const template = document.querySelector(
        `template[data-lux-popup-id="${id}"]`,
    );

    if (!(template instanceof HTMLTemplateElement)) {
        return null;
    }

    const initFn = template.getAttribute("data-lux-popup-init");
    const fragment = /** @type {DocumentFragment} */ (
        template.content.cloneNode(true)
    );
    const element = fragment.firstElementChild;
    template.replaceWith(fragment);

    if (element) {
        luxPopup.popupDismiss(element);
        initLuxDropdowns(element);

        if (element.matches(".lux-modal-nit, .lux-panel")) {
            bindNitClose(element);
        }

        const initCallback = initFn
            ? window.luxPopupInits?.[initFn]
            : undefined;
        if (initCallback) {
            initCallback(element);
        }
    }

    return element;
}

const luxPopup = {
    /**
     * Close a popup element by removing .active and setting aria-hidden.
     *
     * @param {Element} popup - The popup element to close.
     * @returns {void}
     */
    popupClose(popup) {
        popup.classList.remove("active");
        popup.setAttribute("aria-hidden", "true");
        const html = document.querySelector("html");
        if (html) {
            html.classList.remove("no-scroll");
        }
    },
    /**
     * Attach dismiss-click listeners inside a popup.
     *
     * @param {Element} popup - The popup element to attach dismiss handlers to.
     * @returns {void}
     */
    popupDismiss(popup) {
        const toggles = popup.querySelectorAll("*[data-dismiss='lux-popup']");

        toggles.forEach(function (popupDismiss) {
            popupDismiss.addEventListener("click", function (event) {
                event.preventDefault();
                luxPopup.popupClose(popup);
            });
        });
    },
    /**
     * Show a popup by its element ID.
     *
     * @param {string} popupId - The DOM id of the popup element to show.
     * @returns {void}
     */
    popupShow(popupId) {
        const popup = ensurePopupElement(popupId);

        if (popup) {
            const popupSwipers = popup.querySelectorAll(".swiper");

            // Popups cloned from a <template> (see ensurePopupElement) have
            // just entered the DOM, so their off-screen starting position
            // hasn't been committed by the browser yet. Reading a layout
            // property forces that flush before "active" moves it on-screen,
            // so the transition still plays instead of the panel snapping
            // straight into place.
            void (/** @type {HTMLElement} */ (popup).offsetHeight);

            popup.classList.toggle("active");
            popup.setAttribute("aria-hidden", "false");

            popupSwipers.forEach(function (swiperElement) {
                const id = swiperElement.getAttribute("id");

                if (swiperElement.classList.contains("swiper-slider")) {
                    const options = swiperOptions.getSliderOptions(
                        /** @type {HTMLElement} */ (swiperElement),
                    );

                    window.initializeSwiperSlider(id ?? "", options);
                }
            });

            if (
                window.innerWidth < 540 ||
                popup.classList.contains("lux-modal")
            ) {
                const html = document.querySelector("html");
                if (html) {
                    html.classList.add("no-scroll");
                }
            }
        }
    },
    /**
     * Start or stop a delay timer on a popup.
     *
     * @param {Element} popup - The popup element to apply the delay to.
     * @param {"add" | "remove"} state - Whether to add or remove the active class after the delay.
     * @returns {void}
     */
    popupDelayTimer(popup, state) {
        clearTimeout(delayTimer);
        const popupDelay = popup.getAttribute("data-delay");

        if (state === "add") {
            delayTimer = setTimeout(function () {
                popup.classList.add("active");
            }, Number(popupDelay));
        } else {
            delayTimer = setTimeout(function () {
                popup.classList.remove("active");
            }, Number(popupDelay));
        }
    },
    /**
     * Evaluate data-show / data-delay attributes and act accordingly.
     *
     * @param {Element} popup - The popup element to evaluate.
     * @returns {void}
     */
    popupDelay(popup) {
        if (popup.getAttribute("data-show")) {
            if (popup.getAttribute("data-delay")) {
                if (popup.getAttribute("data-show") === "true") {
                    popup.classList.add("active");
                    this.popupDelayTimer(popup, "remove");
                } else if (popup.getAttribute("data-show") === "false") {
                    this.popupDelayTimer(popup, "add");
                }
            }
        } else if (popup.getAttribute("data-delay")) {
            this.popupDelayTimer(popup, "remove");
        }
    },
    /**
     * Show a toast element with auto-dismiss behaviour.
     *
     * @param {Element} toast - The toast element to show.
     * @returns {void}
     */
    toastShow(toast) {
        luxToasts = document.querySelectorAll(".lux-toast");

        if (toast.classList.contains("active")) {
            toast.classList.remove("active");

            for (let toastEl of Array.from(luxToasts)) {
                toastEl.classList.remove("active");
            }

            setTimeout(function () {
                toast.classList.add("active");
            }, 300);
        } else {
            for (let toastEl of Array.from(luxToasts)) {
                toastEl.classList.remove("active");
            }

            toast.classList.add("active");
        }

        this.popupDelay(toast);
        this.popupDismiss(toast);
    },
};

window.luxPopup = luxPopup;

luxPopupToggles.forEach(function (toggle) {
    toggle.addEventListener("click", function (event) {
        event.preventDefault();
        luxPopup.popupShow(toggle.getAttribute("data-target") ?? "");
    });
});

luxPopups.forEach(function (popup) {
    const popupById = document.getElementById(popup.getAttribute("id") ?? "");
    if (popupById) {
        luxPopup.popupDismiss(popupById);
    }
});

luxToasts.forEach(function (toast) {
    const toastById = document.getElementById(toast.getAttribute("id") ?? "");
    if (toastById) {
        luxPopup.popupDelay(toastById);
    }
});

luxModals.forEach(function (modal) {
    const modalById = document.getElementById(modal.getAttribute("id") ?? "");
    if (modalById) {
        luxPopup.popupDelay(modalById);
    }
});

luxPopupNit.forEach(bindNitClose);

export default luxPopup;
